import { ChangeEvent, ReactNode } from 'react';
import { Flex, Icon, Listbox, ProgressBar, SearchField, Text } from '@servicetitan/anvil2';
import IconWarning from '@servicetitan/anvil2/assets/icons/material/round/warning.svg';
import { BottomDrawer } from '../bottom-drawer/bottom-drawer';

export type ItemType<T> = T & {
    label: string;
    disabled?: boolean;
};

export interface BottomDrawerSelectProps<T> {
    open: boolean;
    onClose: () => void;
    dropdownOptions?: ItemType<T>[];
    isLoading?: boolean;
    value: ItemType<T> | undefined;
    onChange: (value: ItemType<T>) => void;
    onSearchQueryChange: (query: string) => void;
    itemToKey: (item: ItemType<T>) => string | number;
    searchPlaceholder?: string;
    getIsItemErrored?: (item: ItemType<T>) => boolean;
    searchInputInfo?: string;
    itemRenderer?: (item: ItemType<T>) => ReactNode;
}

export const BottomDrawerSelect = <T,>(props: BottomDrawerSelectProps<T>) => {
    const {
        dropdownOptions,
        value,
        isLoading = false,
        onChange,
        onSearchQueryChange,
        open,
        onClose,
        searchPlaceholder,
        itemToKey,
        getIsItemErrored,
        searchInputInfo,
        itemRenderer,
    } = props;

    const handleSearchQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
        onSearchQueryChange(e.target.value);
    };

    const handleSearchInputLayout = (inputRef: HTMLInputElement | null) => {
        setTimeout(() => {
            if (inputRef) {
                inputRef.focus();
            }
        }, 0);
    };

    return (
        <BottomDrawer open={open} onClose={onClose}>
            <Flex className="h-100 of-y-hidden" direction="column" gap="2">
                <Flex gap="2" className="p-x-2 p-b-2 p-t-half border-bottom" direction="column">
                    <SearchField
                        autoFocus
                        ref={handleSearchInputLayout}
                        placeholder={searchPlaceholder}
                        onChange={handleSearchQueryChange}
                    />
                    <Text subdued size="small">
                        <i>{searchInputInfo ?? 'Begin typing to view matches'}</i>
                    </Text>
                </Flex>

                {isLoading ? (
                    <Flex justifyContent="space-between" alignItems="center" className="m-4">
                        <ProgressBar indeterminate label="" />
                    </Flex>
                ) : dropdownOptions?.length === 0 ? (
                    <Flex alignItems="center" justifyContent="center" grow="1">
                        No Result Found
                    </Flex>
                ) : (
                    <div className="flex-grow-1 of-y-scroll">
                        <Listbox
                            items={dropdownOptions ?? []}
                            selectionMode="single"
                            selected={value}
                            onSelectionChange={onChange}
                        >
                            {({ items }) =>
                                items.map(item => {
                                    if (getIsItemErrored?.(item)) {
                                        return (
                                            <Listbox.Option key={itemToKey(item)} item={item}>
                                                <Flex direction="row" alignItems="center" flex={1}>
                                                    <Flex flex={1}>
                                                        {itemRenderer?.(item) ?? (
                                                            <Text size="small" subdued>
                                                                {item.label}
                                                            </Text>
                                                        )}
                                                    </Flex>
                                                    <Icon
                                                        color="#E13212"
                                                        svg={IconWarning}
                                                        size="medium"
                                                    />
                                                </Flex>
                                            </Listbox.Option>
                                        );
                                    }
                                    return (
                                        <Listbox.Option key={itemToKey(item)} item={item}>
                                            {itemRenderer?.(item) ?? (
                                                <Text size="small">{item.label}</Text>
                                            )}
                                        </Listbox.Option>
                                    );
                                })
                            }
                        </Listbox>
                    </div>
                )}
            </Flex>
        </BottomDrawer>
    );
};
